import axios from 'axios';
import { lerToken, limparSessao } from './sessao';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Injeta token automaticamente
api.interceptors.request.use((config) => {
  const token = lerToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Rotas do proprio fluxo de autenticacao: 401 aqui e resposta de negocio
// ("credenciais invalidas"), nao sessao expirada. Se o interceptor redirecionar,
// a tela de login pisca e nunca chega a mostrar o erro pro usuario.
const ROTAS_DE_LOGIN = [
  '/auth/login',
  '/auth/register',
  '/auth/phone-code',
  '/auth/signup-config',
  '/auth/external-login',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/reset-password/check',
];

// Rota que valida a sessao ja existente: o 401 dela encerra a sessao sempre.
const ROTA_DE_SESSAO = '/auth/me';

// baseURL pode ser absoluta (REACT_APP_API_URL) ou relativa: compara so o caminho,
// sem protocolo/host e sem querystring.
export const caminhoDaRequisicao = (config) => {
  const bruto = String((config && config.url) || '');
  const semOrigem = bruto.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  return semOrigem.split(/[?#]/)[0];
};

const ehRotaDeLogin = (caminho) => ROTAS_DE_LOGIN.some((rota) => caminho.endsWith(rota));
const ehRotaDeSessao = (caminho) => caminho.endsWith(ROTA_DE_SESSAO);

// Se a requisicao saiu com token. Sem token nao ha sessao para encerrar: o 401 e de
// quem nunca entrou, e a tela (ou o PrivateRoute) decide o que fazer.
export const levouToken = (config) => {
  const cabecalhos = config && config.headers;
  if (!cabecalhos) return false;
  const valor = typeof cabecalhos.get === 'function'
    ? cabecalhos.get('Authorization')
    : cabecalhos.Authorization;
  return Boolean(valor);
};

// Exportada para teste. 'encerrar' = sessao invalida (limpa storage e volta pro login);
// 'propagar' = quem chamou trata o erro.
//
// Nesta API, 401 fora das rotas de login so sai do middleware authenticate: token
// vencido, token invalido ou conta desativada. Permissao, plano e trilha sem acesso
// respondem 403/404. Por isso qualquer 401 de uma chamada que levou token e sessao
// morta. Antes so o /auth/me encerrava, e ele so roda quando o app abre: com a aba
// aberta, o token vencia e a pessoa via "Conteudo nao disponivel" ao abrir a trilha e
// "Token expirado" ao resgatar codigo, sem nunca ser mandada para o login.
export function acaoParaErro(status, caminho, comToken = false) {
  if (status !== 401) return 'propagar';
  if (ehRotaDeLogin(caminho)) return 'propagar';
  if (ehRotaDeSessao(caminho)) return 'encerrar';
  return comToken ? 'encerrar' : 'propagar';
}

// Paginas que abrem sem login (as rotas fora do PrivateRoute no App.jsx). Nelas a sessao
// vencida so e limpa: a landing chama /tracks quando acha token salvo, e mandar para o
// login quem esta lendo a landing ou conferindo um certificado seria atrapalhar — e o
// ?next devolveria a pessoa para a landing em vez do painel.
// As duas do "esqueci minha senha" sao o caso mais comum de token vencido guardado: fora
// da lista, o /auth/me do App mandaria para o login quem abriu o link do e-mail.
const PAGINAS_PUBLICAS = ['/login', '/cadastro', '/bem-vindo', '/esqueci-senha', '/redefinir-senha'];
export const ehPaginaPublica = (caminho) => {
  const limpo = String(caminho || '').replace(/\/+$/, '') || '/';
  return PAGINAS_PUBLICAS.includes(limpo) || limpo.startsWith('/verificar/');
};

// Login com a pagina atual no ?next, para a pessoa voltar ao mesmo lugar depois de
// entrar (quem valida o destino e o Login.jsx). 'sessao=expirada' so liga o aviso.
export function urlDoLogin(local) {
  const destino = `${(local && local.pathname) || '/'}${(local && local.search) || ''}`;
  const params = new URLSearchParams();
  if (destino !== '/') params.set('next', destino);
  params.set('sessao', 'expirada');
  return `/login?${params.toString()}`;
}

// Trava so da NAVEGACAO: a mesma tela dispara varias chamadas em paralelo e, se o
// token morreu, todas voltam 401 juntas — sem ela seria uma navegacao por resposta,
// cada uma cancelando a anterior. So vira true quando a pagina de fato vai ser
// trocada, e a troca zera a flag. Em pagina publica ela nao e ligada: o login seguinte
// e navegacao de SPA, e uma flag presa em true deixaria o proximo 401 sem tratamento.
let redirecionandoParaLogin = false;

// Promessa que nunca resolve: a pagina esta sendo trocada, e deixar o erro seguir faria
// a tela mostrar "Conteudo nao disponivel" ou "Token expirado" no instante antes de sair.
const aguardaANavegacao = () => new Promise(() => {});

// Trata erros globais
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const caminho = caminhoDaRequisicao(error.config);

    // Chamadas que sairam depois da limpeza ja vao sem token e voltam 401 tambem.
    if (redirecionandoParaLogin && status === 401) return aguardaANavegacao();

    if (acaoParaErro(status, caminho, levouToken(error.config)) !== 'encerrar') {
      return Promise.reject(error);
    }

    limparSessao();
    // Em pagina publica limpar basta, e quem chamou (o fetchMe) precisa do erro para
    // zerar o usuario da tela.
    if (ehPaginaPublica(window.location.pathname)) return Promise.reject(error);

    redirecionandoParaLogin = true;
    window.location.assign(urlDoLogin(window.location));
    return aguardaANavegacao();
  }
);

// Tira SO o sufixo /api (com ou sem barra final). O replace('/api','') antigo trocava
// a primeira ocorrencia e destruia hosts como https://api.exemplo.com/api.
export const removeSufixoApi = (url) => String(url).replace(/\/api\/?$/, '');

export const API_BASE_URL = removeSufixoApi(process.env.REACT_APP_API_URL || '/api');

export default api;
